import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, Plus } from "lucide-react";
import { useRunners, useSuites } from "@/api/suites";
import type { Suite, SuiteStatus } from "@/api/types";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const STATUS_STYLE: Record<SuiteStatus, string> = {
  pending: "border-border text-muted-foreground",
  running: "border-sky-500/50 text-sky-500",
  completed: "border-emerald-500/50 text-emerald-500",
  stopped: "border-amber-500/50 text-amber-500",
  failed: "border-red-500/50 text-red-500",
};

export function StatusChip({ status }: { status: SuiteStatus }) {
  return (
    <span className={cn(
      "inline-flex items-center h-5 px-2 rounded-full border text-[10px] font-semibold uppercase tracking-wide",
      STATUS_STYLE[status],
    )}>
      {status}
    </span>
  );
}

/**
 * A one-line summary of where a suite got to.
 *
 * Skipped is shown separately from failed on purpose: a skipped scenario is
 * one the fixture correctly refused to run, and folding it into failures would
 * make a well-behaved suite look broken.
 */
export function Counts({ suite }: { suite: Suite }) {
  const c = suite.counts;
  const parts: [string, number | undefined, string][] = [
    ["done", c.idle, "text-emerald-500"],
    ["running", c.running, "text-sky-500"],
    ["queued", c.queued, "text-muted-foreground"],
    ["failed", c.failed, "text-red-500"],
    ["skipped", c.skipped, "text-amber-500"],
    ["stopped", c.stopped, "text-amber-500"],
  ];
  return (
    <span className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
      {parts.filter(([, n]) => n).map(([label, n, tone]) => (
        <span key={label} className={tone}>{n} {label}</span>
      ))}
      <span>· {suite.total} total</span>
    </span>
  );
}

export function SuitesListPage() {
  const navigate = useNavigate();
  const suites = useSuites();
  const runners = useRunners();
  const runnerName = (id: string) =>
    (runners.data ?? []).find((r) => r.id === id)?.name ?? id;

  const rows = suites.data ?? [];

  return (
    <div className="p-6 space-y-5 max-w-6xl">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Suites</h2>
          <p className="text-sm text-muted-foreground">
            Batches of scenarios fired at one backend, a few at a time.
          </p>
        </div>
        <Button onClick={() => navigate("/suites/new")}>
          <Plus size={14} /> New suite
        </Button>
      </header>

      {rows.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg py-12 text-center space-y-3">
          <p className="text-sm text-muted-foreground">
            {suites.isLoading ? "Loading…" : "No suites yet."}
          </p>
          {!suites.isLoading && (
            <Button onClick={() => navigate("/suites/new")}>
              <Plus size={14} /> New suite
            </Button>
          )}
        </div>
      ) : (
        <div className="border border-border rounded-lg divide-y divide-border bg-card">
          {rows.map((s) => (
            <Link
              key={s.id}
              to={`/suites/${s.id}`}
              className="flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors group"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-foreground">{s.name}</span>
                  <StatusChip status={s.status} />
                </div>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-muted-foreground font-mono">
                    {runnerName(s.runner_id)}
                    {s.backend_kind ? ` · ${s.backend_kind}` : ""}
                  </span>
                  <Counts suite={s} />
                </div>
              </div>
              <ChevronRight
                size={14}
                className="text-muted-foreground group-hover:text-foreground transition-colors"
              />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
